import { OCEAN_STATUS_TOKENS } from './ocean-status-order.mjs?v=20260806.100';

const ACTIVE_ROLES = Object.freeze(['admin', 'dispatcher', 'clerk', 'finance', 'customer']);
const BACK_OFFICE = Object.freeze(['admin', 'dispatcher', 'clerk', 'finance']);
const DISPATCH_CLERK = Object.freeze(['admin', 'dispatcher', 'clerk', 'finance']);
const DISPATCH_ONLY = Object.freeze(['admin', 'dispatcher', 'finance']);
const FINANCE_ONLY = Object.freeze(['admin', 'finance']);

// Dates dropped the year in the table, so every date track is narrower than it
// was. Note grew because the text is now shown in the cell instead of behind a
// button.
// Re-measured 2026-08-10 after the sort icon left the header and the seconds
// left the value, in Rubik at the sizes the table actually uses (12px cells,
// 9.5px tracked caps) plus 8px of padding:
//
//   date-only   content "Dec 31" 42px, widest label "Created" 64px  -> 72
//   date-time   content "Jul 30, 11:45 PM" 105px (124 with seconds),
//               widest label was "Actual Return" 115px               -> 123
//
// The icon was never making these columns wider - table-layout is fixed, so it
// was eating the label's share of a track the registry had already set, which is
// why "Actual Return" was cut off. It fits now, and the track still shrinks.
//
// "Actual Return" is since renamed "Return Appt" (Alan, 2026-09-29) - shorter,
// so 123 is still a safe upper bound; nothing here needed re-measuring.
const COLUMN_WIDTHS = Object.freeze({
  'date-only': Object.freeze({ minWidth: 74, preferredWidth: 78 }),
  'date-time': Object.freeze({ minWidth: 124, preferredWidth: 128 }),
  money: Object.freeze({ minWidth: 76, preferredWidth: 82 }),
  driver: Object.freeze({ minWidth: 96, preferredWidth: 104 }),
  address: Object.freeze({ minWidth: 112, preferredWidth: 122 }),
  note: Object.freeze({ minWidth: 140, preferredWidth: 152 }),
});

/**
 * Narrower date columns for the Chinese grid.
 *
 * Measured on the deployed table with the real fonts (2026-08-11), not guessed:
 * the same instant is `12月30日` at 60px in Chinese and `Dec 30, 2026` at 81px in
 * English, and with the time on it, 94px against 118px. Every date column was
 * sized for the English string. Alan reads the Chinese UI, where that difference
 * arrived as 26-42px of dead air beside every date - "way too big gap".
 *
 * English is the default locale and its numbers are deliberately untouched: they
 * are correct for the string English renders, and shrinking them would clip the
 * year. So this is a Chinese-only override rather than a new width.
 *
 * Each number is `max(header, Chinese value) + 6`. The header is still the
 * English one, which is the conservative side - a Chinese header is shorter, so
 * it cannot be the thing that overflows. The 6px is on purpose and is not slack
 * to be reclaimed later: Alan asked for the columns to close up, "but there
 * should still be gap between so we dont mis read information".
 *
 * Columns absent from this list are absent because the override would have made
 * them WIDER than they are today - transfer_at, return_empty_lfd_at and
 * updated_at have long headers - and this change only ever narrows.
 */
const CJK_COLUMN_WIDTHS = Object.freeze({
  created_at: Object.freeze({ minWidth: 62, preferredWidth: 66 }),
  eta_at: Object.freeze({ minWidth: 62, preferredWidth: 66 }),
  lfd_at: Object.freeze({ minWidth: 62, preferredWidth: 66 }),
  special_fee_date: Object.freeze({ minWidth: 88, preferredWidth: 90 }),
  special_fee_2_date: Object.freeze({ minWidth: 88, preferredWidth: 90 }),
  special_fee_3_date: Object.freeze({ minWidth: 88, preferredWidth: 90 }),
  pickup_appointment_at: Object.freeze({ minWidth: 96, preferredWidth: 100 }),
  actual_empty_returned_at: Object.freeze({ minWidth: 96, preferredWidth: 100 }),
  reported_empty_at: Object.freeze({ minWidth: 150, preferredWidth: 154 }),
  empty_returned_at: Object.freeze({ minWidth: 105, preferredWidth: 109 }),
});

/**
 * The width a column is drawn at, which depends on the language it is read in.
 *
 * Resolved per render rather than baked into the frozen column, because the
 * locale changes while the table is on screen and the column objects do not.
 */
export function columnWidthsForLocale(column, locale) {
  const chinese = String(locale || '').toLowerCase().startsWith('zh');
  const override = chinese ? CJK_COLUMN_WIDTHS[column.key] : null;
  return override || { minWidth: column.minWidth, preferredWidth: column.preferredWidth };
}

/**
 * What the open menus have learned, and how a column's menu is assembled.
 *
 * The seeded list in FIELD_BEHAVIORS is a floor: the values production held when
 * the menu was built, in most-used order, because that order is what makes a menu
 * read fast. Everything else the fleet carries is appended, alphabetically, from
 * `summary.fieldOptions` - which the callable counts on a walk it was already
 * making, so a Custom value is on a Container the moment it saves and in
 * everybody's menu on their next load (Alan, 2026-08-21: "每次有一个 custom 就加
 * 一个进下拉库里面"). There is no library to curate and nothing to keep in step
 * with the data, because the data IS the library.
 *
 * Module state rather than a prop threaded through both editors: the table's
 * floating menu and the Task page's suggestion list are two surfaces that must
 * never offer different lists, and a third surface would have to be wired
 * separately. Set once when the summary lands.
 *
 * The consequence, stated because it is a real one: a value used by no Container
 * is in no menu. Correct the last row spelling "COSCOO" and it stops being
 * offered; that is the same rule working, not a value being lost - the cell and
 * the editor both still draw whatever a row actually holds.
 */
// Pinned to globalThis, not to this module, and that is not decoration.
//
// The browser imports this file with the shared cache key on the specifier.
// Anything that imports it under a DIFFERENT specifier - a test importing the
// bare path, a future caller that forgets the key - gets a second module
// instance with its own module-level state. The menus would then split in half:
// the table learning a value the Task page has never heard of, with nothing
// failing. One Map on globalThis is one vocabulary however many times the module
// is instantiated.
const LEARNED_FIELD_OPTIONS = (globalThis.__kmTmsLearnedFieldOptions ??= new Map());

export function setLearnedFieldOptions(vocabulary) {
  LEARNED_FIELD_OPTIONS.clear();
  if (!vocabulary || typeof vocabulary !== 'object') return;
  for (const [key, values] of Object.entries(vocabulary)) {
    if (!Array.isArray(values)) continue;
    const cleaned = values.map((value) => String(value ?? '').trim()).filter(Boolean);
    if (cleaned.length) LEARNED_FIELD_OPTIONS.set(key, cleaned);
  }
}

export function fieldOptions(column) {
  const seeded = Array.isArray(column?.options) ? column.options : [];
  // Closed menus stay closed: ocean_status and the fee statuses are tokens the
  // server validates, and a value learned from data has no business in them.
  if (!column?.allowCustom) return seeded;
  const learned = LEARNED_FIELD_OPTIONS.get(column.key) ?? [];
  if (!learned.length) return seeded;
  const seen = new Set(seeded.map((value) => String(value).toUpperCase()));
  return [...seeded, ...learned.filter((value) => !seen.has(String(value).toUpperCase()))];
}

const FIELD_BEHAVIORS = Object.freeze({
  // A customer must not be able to rename themselves (Alan, 2026-08-01). It also
  // became a cross-tenant hole the moment the Delivery Address dropdown started
  // filtering on this value: editing it to another company's name would surface
  // that company's approved addresses and their prices. Empty list, so the standing
  // Admin override in columnEditableForRole still allows the corrections that need
  // making, and the customer keeps the change-request path
  // (customer_change_requestable stays true in container-field-policy.ts).
  customer_name: { editRoles: [], editor: 'text' },
  // Same rule as customer_name (Alan, 2026-08-01): the two identity fields a
  // customer must not rewrite after the Task exists. Everything downstream - the
  // dispatch task, the POD evidence, the finance summary - is keyed to this
  // Container, so a silent edit re-points paperwork that has already been acted on.
  // Admin keeps the correction via the standing override, and the customer keeps
  // the change-request route (customer_change_requestable is true).
  // Reopened to the customer until the Task is billed (Alan, 2026-08-03). The
  // fee-settled lock in columnEditableForRow shuts it the moment CTF or TMF is Paid.
  // The clerk has every column the customer has (Alan, 2026-09-25: "文员有所有顾客的
  // 权限同步到文员"): this, size_type and customer_note were the three missing.
  container_no: { editRoles: ['customer', 'clerk'], editor: 'text', interaction: 'copy' },
  pin: { editRoles: ['dispatcher'], editor: 'text', interaction: 'copy' },
  // Clerk edits Forecast Status as well as the customer (Alan, 2026-08-12). The
  // matching authority is edit_roles on pre_alert_status in
  // functions/src/container-field-policy.ts - this list only draws the control.
  pre_alert_status: { editRoles: ['customer', 'clerk'], editor: 'select', options: ['Pre-Alert', 'Pre-Alert + DO'] },
  // Whether the CUSTOMER brought their own chassis. Admin only — the standing
  // override in columnEditableForRole is the permission, so editRoles stays empty
  // (Alan, 2026-08-09).
  //
  // The token means what it says, and has since 2026-08-10 (Alan: "yes 就是 own
  // chassis, no 就是不是 own chassis"): "yes" is the customer's own chassis, so
  // the column reads Yes and there is no Chassis # for us to fill in. It used to
  // be the inverse — a record of what WE did — and all 498 rows were cleared to
  // blank when it changed, so no stored value carries the old meaning.
  // rowUsesCustomerOwnChassis() below is what gates the Chassis # editor on the
  // row, and the callable mirrors it.
  customer_chassis: { editRoles: [], editor: 'select', options: ['yes', 'no'] },
  master_bl_no: { editRoles: ['customer', 'clerk'], editor: 'text', interaction: 'copy' },
  // CUSTOM is listed rather than appended: it has been the fourth chip in this
  // menu since 2026-08-08 and belongs where the operator's hand already goes.
  size_type: { editRoles: ['customer', 'clerk'], editor: 'select', allowCustom: true, options: ['20', '40HQ', '45', 'CUSTOM'] },
  // Clerk edits Weight as well as the customer (Alan, 2026-08-12). Authority is
  // edit_roles on weight_lbs in functions/src/container-field-policy.ts.
  weight_lbs: { editRoles: ['customer', 'clerk'], editor: 'number', interaction: 'copy' },
  // Menus, not free text (Alan, 2026-08-21) - and both keep a Custom entry, so the
  // list is a floor and never a ceiling. `allowCustom` is what says so: the menu
  // gets a Custom option that opens a box, the value typed there is stored as
  // typed, and no writer refuses it (ssl and terminal_name have no validator in
  // functions/src/container-field-policy.ts - they are plain text on the server
  // and stay that way).
  //
  // The options are what production actually holds, counted 2026-08-21 over 802
  // Containers, most-used first, because the menu is read top-down all day:
  // COSCO alone is 177 of the 531 that carry a shipping line.
  //
  // Two deliberate omissions, both worth knowing:
  //   - "COSCOO" (1 row) is a typo of COSCO and is left off. The row still shows
  //     it - selectOptionsMarkup and the cell both draw a stored value that is
  //     not in the list - so nothing is hidden, it is just not offered again.
  //     A menu that offers the typo is a menu that keeps making it.
  //   - "暂无" (105 rows) is kept but placed LAST. It means "no terminal yet",
  //     which is what the menu's own clear option already means; leading with it
  //     would make the easiest click the one that says nothing.
  ssl: {
    editRoles: ['clerk'],
    editor: 'select',
    allowCustom: true,
    options: ['COSCO', 'OOCL', 'ZIM', 'HEDE', 'EGLV', 'WHL', 'CMA', 'SML', 'MSC',
      'ONE', 'HMM', 'MSK', 'MATU', 'YML', 'HPL', 'KMCT'],
  },
  terminal_name: {
    editRoles: ['clerk'],
    editor: 'select',
    allowCustom: true,
    // "c-60" is listed exactly as it is stored, lower case and all: an option that
    // differs from the stored value by case is a second entry for one terminal.
    options: ['PCT', 'LBCT', 'WBCT', 'APM', 'TRAPAC', 'YTI', 'ITS', 'ETS',
      'PIER A', 'FMS', 'TTI', 'c-60', '暂无'],
  },
  ctf_tmf_status: { editRoles: ['finance'], editor: 'ctf_tmf' },
  ocean_status: {
    // Dispatch moves the Container through these states as much as the clerk does,
    // so both edit it (Alan, 2026-08-01).
    editRoles: ['clerk', 'dispatcher'], editor: 'select',
    // The nine statuses Alan named, in the order he named them (2026-08-09). The
    // menu is the same list the table sorts by, so what you read down the column
    // and what you read down the menu are one order.
    options: OCEAN_STATUS_TOKENS,
  },
  // Two notes, not one (Alan, 2026-08-02). The internal half keeps the field name
  // and therefore keeps everything already written in it; the customer reads it on
  // hover - the read-only note cell already renders the full text in a tooltip -
  // but no longer edits it. The customer half is new and starts empty.
  // HOLD, its own text box beside the Note (Alan, 2026-10-12); same editors as the Note, never the customer.
  hold_status: { editRoles: ['clerk', 'finance', 'dispatcher'], editor: 'text' },
  hold_note: { editRoles: ['clerk', 'finance', 'dispatcher'], editor: 'note', interaction: 'note' },
  customer_note: { editRoles: ['customer', 'clerk'], editor: 'note', interaction: 'note' },
  eta_at: { editRoles: ['clerk'], editor: 'date' },
  // Clerk edits LFD as well as Dispatch (Alan, 2026-08-11): the clerk takes the
  // terminal's call when the free time moves, and was having to relay it to
  // dispatch to be typed in. The matching authority is edit_roles on lfd_at in
  // functions/src/container-field-policy.ts - this list only draws the control.
  lfd_at: { editRoles: ['dispatcher', 'clerk'], editor: 'date' },
  chassis_no: { editRoles: ['dispatcher'], editor: 'text', interaction: 'copy' },
  pickup_appointment_at: { editRoles: ['dispatcher'], editor: 'date' },
  // The customer picks this once, at Add Task, from their approved locations — and the quote shown
  // beside it is priced against that address. That is why this list was empty until 2026-08-17:
  // no internal role could change it and only the standing Admin override could (Alan, 2026-07-31).
  //
  // Dispatch joined on 2026-08-17 ("delivery address, dispatcher也要能改") and the clerk on
  // 2026-08-21 ("我需要文员有也有权限点击delivery address column可以直接勾新的"), both told first
  // that moving this moves the address a quote was priced against and that nothing re-prices it.
  // He reaffirmed each time, so the rule changed rather than the answer being softened.
  //
  // Clicking gives the approved list, not a text box: the editor is `address`, filled from the
  // customer's own quote locations, and the clerk has been able to READ that list since
  // 2026-08-07 - the same read their Add Task menu uses. So this is the only gate that was shut.
  // The matching authority is edit_roles on delivery_warehouse_address in
  // functions/src/container-field-policy.ts — this list only draws the control.
  delivery_warehouse_address: { editRoles: ['dispatcher', 'clerk'], editor: 'address', interaction: 'address' },
  // Only Delivery Address picks from a catalog — it is the one address that is priced, so it has
  // to come from the customer's approved list. Every other address is somewhere a driver was sent
  // once; typing it is faster than hunting a dropdown that can never be complete
  // (Alan, 2026-08-01). They keep interaction: 'address' so the map still opens on the value.
  // Clerk too (Alan, 2026-09-29): Report Empty takes the place with it ("9/28空611"),
  // and whoever reports the empty must be able to say where it is.
  empty_from_address: { editRoles: ['dispatcher', 'clerk'], editor: 'text', interaction: 'address' },
  // Empty To is the terminal the empty goes back to, so it picks from the terminal
  // list instead of being typed (Alan, 2026-08-01). Catalog-backed like Delivery
  // Address, but scoped to terminals rather than to the row's customer.
  //
  // It is the one address column with no map and no Custom entry (Alan,
  // 2026-08-05). The terminals are a closed list: a driver returns an empty to
  // one of them or the return is wrong, so there is nothing to type and nothing
  // to look up on a map. `interaction` is therefore dropped and the width group
  // named on the field() call, which is what `interaction: 'address'` supplied.
  empty_to_address: { editRoles: ['dispatcher'], editor: 'address' },
  paired_empty_from_address: { editRoles: [], editor: null },
  paired_empty_to_address: { editRoles: [], editor: null },
  empty_container_no: { editRoles: ['dispatcher'], editor: 'text', interaction: 'copy' },
  return_driver_name: { editRoles: ['dispatcher'], editor: 'driver' },
  return_cost_cents: { editRoles: ['finance'], editor: 'currency' },
  pickup_driver_name: { editRoles: ['dispatcher'], editor: 'driver' },
  pickup_cost_cents: { editRoles: ['finance'], editor: 'currency' },
  transfer_driver_name: { editRoles: ['dispatcher'], editor: 'driver' },
  transfer_at: { editRoles: ['dispatcher'], editor: 'date' },
  transfer_from_address: { editRoles: ['dispatcher'], editor: 'text', interaction: 'address' },
  transfer_to_address: { editRoles: ['dispatcher'], editor: 'text', interaction: 'address' },
  transfer_cost_cents: { editRoles: ['finance'], editor: 'currency' },
  // Clerk reports the empty too (Alan, 2026-08-02). It is a floor observation --
  // the box is empty -- not a dispatch decision, and the clerk is often the one
  // standing there. Hiding the button from them only meant it got phoned in.
  reported_empty_at: { editRoles: ['customer', 'dispatcher', 'clerk'], editor: 'date' },
  // Where the reported empty is - shown inside the Reported Empty cell, not a column of its own.
  reported_empty_place: { editRoles: ['dispatcher', 'clerk'], editor: 'text' },
  return_empty_lfd_at: { editRoles: ['dispatcher'], editor: 'date' },
  actual_empty_returned_at: { editRoles: ['dispatcher'], editor: 'actual_empty_return' },
  empty_returned_at: { editRoles: [], editor: null },
  pickup_company: { editRoles: ['clerk', 'dispatcher'], editor: 'text' },
  pod_count: { editRoles: [], editor: null, interaction: 'pod' },
  exception_fee_cents: { editRoles: ['finance'], editor: 'currency' },
  // Seeded from the customer's approved quote locations - the price they were shown
  // when they picked the delivery address - and correctable here by Finance, with
  // Admin arriving through the standing override (Alan, 2026-08-21: "finance和admin
  // 可以更改或者添加或者删除"). Read-only for everyone until then, by his rule of
  // 2026-08-03; the catalog is still where a customer's price list is maintained,
  // and this is where one Container's number is fixed.
  //
  // A blank clears it, like every other column a role may change - customer_quote_cents
  // is not in NEVER_CLEARABLE_FIELDS, so "删除" needs nothing of its own.
  customer_quote_cents: { editRoles: ['finance'], editor: 'currency' },
  customer_receivable_cents: { editRoles: ['finance'], editor: 'currency' },
  remittance_status: { editRoles: ['finance'], editor: 'select', options: ['received', 'not_received'] },
  special_fee_date: { editRoles: ['finance'], editor: 'date' },
  // A Special Fee is always attributed to a driver, so all three Name fields pick
  // from the driver list rather than being typed (Alan, 2026-08-02). Same editor the
  // dispatch driver fields use; the stored value is still the driver's name as text,
  // so the finance_summary field and its server policy are unchanged.
  special_fee_name: { editRoles: ['finance'], editor: 'driver' },
  special_fee_amount_cents: { editRoles: ['finance'], editor: 'currency' },
  special_fee_detail: { editRoles: ['finance'], editor: 'note', interaction: 'note' },
  special_fee_2_name: { editRoles: ['finance'], editor: 'driver' },
  special_fee_2_date: { editRoles: ['finance'], editor: 'date' },
  special_fee_2_amount_cents: { editRoles: ['finance'], editor: 'currency' },
  special_fee_2_detail: { editRoles: ['finance'], editor: 'note', interaction: 'note' },
  special_fee_3_name: { editRoles: ['finance'], editor: 'driver' },
  special_fee_3_date: { editRoles: ['finance'], editor: 'date' },
  special_fee_3_amount_cents: { editRoles: ['finance'], editor: 'currency' },
  special_fee_3_detail: { editRoles: ['finance'], editor: 'note', interaction: 'note' },
  // Shown, never typed: the price list's own words for the booked address (2026-10-12).
  customer_quote_terms: { editRoles: [], editor: null, interaction: 'note' },
  customer_special_fee_cents: { editRoles: ['finance'], editor: 'currency' },
  service_fee_cents: { editRoles: ['finance'], editor: 'currency' },
  customer_special_fee_detail: { editRoles: ['finance'], editor: 'note', interaction: 'note' },
  // CTF $20 + TMF $81.26, each dropped when the customer settled it directly
  // (Alan, 2026-08-03). Derived, so nobody types it.
  pier_pass_cents: { editRoles: [], editor: null },
});

function field(key, label, priority, roles, options = {}) {
  const behavior = FIELD_BEHAVIORS[key] || {};
  const widthGroup = options.widthGroup
    || (behavior.interaction === 'note' ? 'note' : null)
    || (behavior.interaction === 'address' ? 'address' : null)
    || (behavior.editor === 'driver' ? 'driver' : null)
    || (options.format === 'currency' ? 'money' : null)
    || (options.format === 'date-only' ? 'date-only' : null)
    || (options.format === 'date-time' ? 'date-time' : null);
  // An explicit width always wins over the shared track. Identifier columns use
  // this to guarantee their full value fits: a truncated Container or MBL number
  // is worse than a wide column, whereas a truncated label is merely terse.
  const sharedWidth = options.minWidth ? null : COLUMN_WIDTHS[widthGroup] || null;
  return Object.freeze({
    key,
    label,
    headerLabel: options.headerLabel || label,
    priority,
    identifier: options.identifier === true,
    roles: Object.freeze([...new Set(roles)]),
    minWidth: sharedWidth?.minWidth || options.minWidth || 104,
    preferredWidth: sharedWidth?.preferredWidth || options.preferredWidth || options.minWidth || 122,
    widthGroup,
    sticky: options.sticky || false,
    // Stays in the registry - and so in the Task detail modal, which builds its field
    // sections from the same list - but the table skips it. See authorizedColumns.
    detailOnly: options.detailOnly === true,
    format: options.format || 'text',
    chip: options.chip === true,
    // { head, tail }: draw the value with its middle elided and the whole thing
    // on hover. Only for identifiers long enough that the column costs more than
    // the reading of it — see abbreviateIdentifier.
    abbreviate: options.abbreviate ? Object.freeze({ ...options.abbreviate }) : null,
    editor: behavior.editor || null,
    interaction: behavior.interaction || null,
    editRoles: Object.freeze([...(behavior.editRoles || [])]),
    options: Object.freeze([...(behavior.options || [])]),
    // A menu whose options are a starting point, not the whole world: the editor
    // adds a Custom entry that opens a box for anything not listed. Also tells
    // the two editors that these options are DATA rather than tokens, so they are
    // drawn verbatim - "COSCO" is a carrier's name, and Title Casing it to
    // "Cosco" would be drawing something nobody wrote.
    allowCustom: behavior.allowCustom === true,
  });
}

// Headings are Chinese (Alan, 2026-10-12, a list: "Container改成 柜号 ... Special Fee 1 note 特殊费用1备注"); `label`
// stays English - it is the hover title, the Task detail's field name and the backup sheet's heading. Fee 2 and 3
// follow fee 1's pattern; "Amount" is 金额 so it is not a second "特殊费用1" beside the name.
// Global order is authoritative. Role-hidden fields are removed before rendering,
// so the remaining columns always close left without blank placeholders.
export const CONTAINER_FIELD_REGISTRY = Object.freeze([
  field('customer_name', 'Customer Name', 'P0', ACTIVE_ROLES, { minWidth: 112, preferredWidth: 126, headerLabel: '客户' }),
  field('container_no', 'Container #', 'P0', ACTIVE_ROLES, { minWidth: 124, preferredWidth: 132, sticky: 'left', identifier: true, headerLabel: '柜号' }),
  field('created_at', 'Creation Date', 'P2', ['admin', 'dispatcher', 'clerk', 'finance'], { format: 'date-only', headerLabel: '日期预报' }),
  // Header labels are short enough to sit whole inside the compressed track:
  // a truncated label is allowed, but a legible one is better when it is free.
  // Chip column, so two pixels of gutter (Alan, 2026-08-11). Was 38/40 for a 38px chip under "CHS"; the header
  // "是否带车架" (2026-10-12) measures 59px, so the header is the floor now.
  field('customer_chassis', 'Customer Chassis', 'P2', ACTIVE_ROLES, { minWidth: 72, preferredWidth: 74, headerLabel: '是否带车架', chip: true }),
  // A PIN is four digits: 36px of tabular figures under a 29px header, so 62 was
  // twenty pixels of nothing (Alan, 2026-08-11). Plain text, so six of gutter.
  field('pin', 'PIN', 'P2', DISPATCH_ONLY, { minWidth: 38, preferredWidth: 42, identifier: true, headerLabel: '密码' }),
  // Widened, not narrowed: measured 97px for "Pre-Alert + DO" in a 56px column,
  // so the longer of the two states was being cut in half (Alan, 2026-08-11,
  // after the gap pass turned this up). Chip, so two pixels of gutter.
  field('pre_alert_status', 'Forecast Status', 'P1', ['admin', 'dispatcher', 'clerk', 'finance', 'customer'], { minWidth: 97, preferredWidth: 99, headerLabel: '预报', chip: true }),
  // The MBL runs 10-17 characters and every one of the 497 in production is
  // distinct, so the column was sized for the longest and read for none of it.
  // Eliding the middle to first-4 + last-5 costs 10 characters instead of 17 and
  // leaves 4 rows in 497 sharing a rendering; cutting to the leading letters
  // alone, which is the obvious move, would have left 150 rows all reading
  // "COSU" (measured against production, 2026-08-10). Full value on hover.
  // Widths measured in the real face, not guessed: Rubik 12px tabular, 4px of
  // padding each side. The abbreviation is always 10 characters, worst case
  // "MMMM…88888" at 82px, so 92 leaves 2px and 96 leaves 6. The full 17-character
  // value needs 123px, which is what the old 126/134 was paying for.
  field('master_bl_no', 'MBL #', 'P1', ACTIVE_ROLES, {
    minWidth: 92, preferredWidth: 96, identifier: true, abbreviate: { head: 4, tail: 5 }, headerLabel: '提单号',
  }),
  // 65px for "CUSTOM" in a 50px column. Widened for the same reason as FCST.
  field('size_type', 'Container Type', 'P1', ACTIVE_ROLES, { minWidth: 65, preferredWidth: 67, headerLabel: '尺寸' }),
  field('weight_lbs', 'Weight LBS', 'P2', ['admin', 'dispatcher', 'clerk', 'finance', 'customer'], { format: 'number', minWidth: 66, headerLabel: '重量磅' }),
  field('ssl', 'Shipping Line', 'P2', ['admin', 'dispatcher', 'clerk', 'finance', 'customer'], { minWidth: 62, headerLabel: '船司' }),
  field('terminal_name', 'Terminal', 'P1', ['admin', 'dispatcher', 'clerk', 'finance', 'customer'], { minWidth: 56, headerLabel: '码头' }),
  // Measured 2026-08-10: the two chips need 62px, the cell was 128, so 66px of
  // every row was blank between TMF and Ocean Status (Alan: "这两个距离太远了").
  // The floor is the header, not the chips - "CTF & TMF" needs 80px including
  // padding - so 82/86 closes about 42px of the gap and still spells the label.
  // 71/73 measured, not guessed: the two chips are 71px and the header 67px, so
  // 86 left 15px of dead air on a column whose content is two fixed words
  // (Alan, 2026-08-11: "too much gap"). Chip text is the same in both locales,
  // so this needs no Chinese override. Two pixels of gutter rather than the six
  // a text column gets - a chip draws its own border, so the eye already has the
  // edge it needs and the columns "can just be really close" (Alan).
  field('ctf_tmf_status', 'CTF & TMF', 'P2', BACK_OFFICE, { minWidth: 71, preferredWidth: 73, headerLabel: 'CTF & TMF' }),
  // 63px is the widest status pill there is ("pending"; every translated one is
  // 51px) against a 46px header, so 76 was carrying 13px of nothing (Alan,
  // 2026-08-11: "这两个space太大了"). Chip column, so two pixels of gutter - the
  // pill's own edge is the separation. The labels are Chinese in both locales,
  // which is why this needs no per-locale override.
  field('ocean_status', 'Ocean Status', 'P0', ['admin', 'dispatcher', 'clerk', 'finance', 'customer'], { minWidth: 63, preferredWidth: 65, headerLabel: '状态', chip: true }),
  // Staff only (Alan, 2026-10-10: "这个客户端页面左边那个note不让客户看到"): the customer sees Cust Note, their own,
  // beside it - never this internal Note. functions/src/role-container-dashboard.ts and container-field-policy.ts
  // refuse to send it to a customer too; this list only decides what is drawn.
  // HOLD on its own (Alan, 2026-10-12: "能否将hold单独列出来？note就备注其他一些重要的信息...以免删hold时不小心删除").
  // Everyone reads it - the customer too - and clearing it leaves the Note untouched. "CUSTOMS HOLD LINE HOLD" fits.
  field('hold_status', 'HOLD', 'P1', ACTIVE_ROLES, { minWidth: 150, preferredWidth: 156 }),
  field('hold_note', 'Note', 'P1', ['admin', 'dispatcher', 'clerk', 'finance'], { headerLabel: 'Note' }),
  field('customer_note', 'Customer Note', 'P1', ['admin', 'dispatcher', 'clerk', 'finance', 'customer'], { headerLabel: 'Cust Note' }),
  field('eta_at', 'ETA', 'P0', ['admin', 'dispatcher', 'clerk', 'finance', 'customer'], { format: 'date-only' }),
  // Everyone sees LFD (Alan, 2026-08-08) - it is the deadline the whole job is
  // timed against, and the clerk who takes the port's calls was the one role
  // without it. Clerk may now change it too (Alan, 2026-08-11) - taking the call
  // and not being able to act on it was half a permission. Kept in step with
  // LFD_VISIBLE_ROLES and LFD_VISIBLE on the server; a column without the
  // projection behind it renders an empty cell on every row.
  field('lfd_at', 'LFD', 'P0', ACTIVE_ROLES, { format: 'date-only' }),
  // Hidden from customers entirely (Alan, 2026-08-02): the chassis is ours, not
  // theirs, and a customer bringing their own has no Chassis # to look at anyway.
  field('chassis_no', 'Chassis #', 'P1', BACK_OFFICE, { minWidth: 106, identifier: true, headerLabel: '车架' }),
  // The DO is a link the customer supplies at Add Task (Alan, 2026-08-02). It is
  // registered so the Task detail can render it - the modal only shows fields the
  // role's column list knows about - and it renders as a real anchor there.
  // DO and POD come out of the table and live in the Task detail modal (Alan,
  // 2026-08-02). Neither is lost by leaving: the modal already carries a whole POD
  // section with its own uploader, and the DO has been a link on the detail since
  // f33cfa6. What goes is only the two columns' width in a 410-row table.
  field('do_ref', 'DO', 'P3', ACTIVE_ROLES, { minWidth: 120, detailOnly: true }),
  field('pickup_appointment_at', 'Pickup Appointment', 'P1', ACTIVE_ROLES, { format: 'date-time', headerLabel: '重柜约' }),
  field('delivery_warehouse_address', 'Delivery Address', 'P2', ACTIVE_ROLES, { headerLabel: '送仓地址' }),
  // Pickup Driver sits straight after Delivery Address: you read where it goes, then
  // who takes it (Alan, 2026-08-01). Its cost stays with it.
  field('pickup_driver_name', 'Pickup Driver', 'P1', DISPATCH_CLERK, { minWidth: 120, headerLabel: '提重司机' }),
  field('pickup_cost_cents', 'Pickup Cost', 'P3', FINANCE_ONLY, { format: 'currency', headerLabel: '重柜费用' }),
  field('empty_from_address', 'Empty From', 'P2', DISPATCH_CLERK),
  field('empty_to_address', 'Empty To Address', 'P2', DISPATCH_CLERK, { widthGroup: 'address' }),
  // Detail-only: the matched empty's own legs belong in the Task, not as two more
  // columns in a table that is already too wide (Alan, 2026-08-03).
  field('paired_empty_from_address', 'Empty From (paired)', 'P3', BACK_OFFICE, { detailOnly: true }),
  field('paired_empty_to_address', 'Empty To (paired)', 'P3', BACK_OFFICE, { detailOnly: true }),
  field('empty_container_no', 'Empty Container #', 'P2', DISPATCH_CLERK, { minWidth: 108, headerLabel: '配空柜号', identifier: true }),
  field('return_driver_name', 'Return Driver', 'P2', DISPATCH_ONLY, { minWidth: 120, headerLabel: '还空司机' }),
  field('return_cost_cents', 'Return Cost', 'P3', FINANCE_ONLY, { format: 'currency', headerLabel: '还空费用' }),
  field('transfer_driver_name', 'Transfer Driver', 'P2', DISPATCH_CLERK, { minWidth: 120, headerLabel: '转仓司机' }),
  field('transfer_at', 'Transfer Date', 'P2', DISPATCH_CLERK, { format: 'date-only', headerLabel: '转仓日期' }),
  field('transfer_from_address', 'Transfer From', 'P3', DISPATCH_CLERK, { headerLabel: '转仓起点' }),
  field('transfer_to_address', 'Transfer To', 'P3', DISPATCH_CLERK, { headerLabel: '转仓终点' }),
  field('transfer_cost_cents', 'Transfer Cost', 'P3', FINANCE_ONLY, { format: 'currency', headerLabel: '转仓费用' }),
  field('special_fee_name', 'Special Fee 1 Name', 'P3', FINANCE_ONLY, { minWidth: 120, headerLabel: '特殊费用1' }),
  field('special_fee_date', 'Special Fee 1 Date', 'P3', FINANCE_ONLY, { format: 'date-only', minWidth: 88, preferredWidth: 90, headerLabel: '特殊费用1日期' }),
  field('special_fee_amount_cents', 'Special Fee 1 Amount', 'P3', FINANCE_ONLY, { format: 'currency', minWidth: 88, preferredWidth: 90, headerLabel: '特殊费用1金额' }),
  field('special_fee_detail', 'Special Fee 1 Note', 'P3', FINANCE_ONLY, { headerLabel: '特殊费用1备注' }),
  field('special_fee_2_name', 'Special Fee 2 Name', 'P3', FINANCE_ONLY, { minWidth: 120, headerLabel: '特殊费用2' }),
  field('special_fee_2_date', 'Special Fee 2 Date', 'P3', FINANCE_ONLY, { format: 'date-only', minWidth: 88, preferredWidth: 90, headerLabel: '特殊费用2日期' }),
  field('special_fee_2_amount_cents', 'Special Fee 2 Amount', 'P3', FINANCE_ONLY, { format: 'currency', minWidth: 88, preferredWidth: 90, headerLabel: '特殊费用2金额' }),
  field('special_fee_2_detail', 'Special Fee 2 Note', 'P3', FINANCE_ONLY, { headerLabel: '特殊费用2备注' }),
  field('special_fee_3_name', 'Special Fee 3 Name', 'P3', FINANCE_ONLY, { minWidth: 120, headerLabel: '特殊费用3' }),
  field('special_fee_3_date', 'Special Fee 3 Date', 'P3', FINANCE_ONLY, { format: 'date-only', minWidth: 88, preferredWidth: 90, headerLabel: '特殊费用3日期' }),
  field('special_fee_3_amount_cents', 'Special Fee 3 Amount', 'P3', FINANCE_ONLY, { format: 'currency', minWidth: 88, preferredWidth: 90, headerLabel: '特殊费用3金额' }),
  field('special_fee_3_detail', 'Special Fee 3 Note', 'P3', FINANCE_ONLY, { headerLabel: '特殊费用3备注' }),
  field('total_expense_cents', 'Total Expense', 'P2', FINANCE_ONLY, { format: 'currency', headerLabel: '合计支出' }),
  // Reported Empty sits immediately before Return Empty LFD (Alan, 2026-08-02, moved
  // from after Delivery Address): reporting the empty is what starts the return clock,
  // so the two read together.
  // An explicit width here only repeated the shared date-time track; dropping it
  // means this column shrinks with the others instead of being missed.
  // 150px: the cell reads "10月1日 05:00 · 611" once a place was typed with the report (Alan, 2026-10-11).
  field('reported_empty_at', 'Reported Empty', 'P1', ACTIVE_ROLES, { format: 'date-time', minWidth: 172, headerLabel: '报空日期地点' }),
  field('return_empty_lfd_at', 'Return Empty LFD', 'P2', DISPATCH_CLERK, { format: 'date-only', headerLabel: '空柜LFD' }),
  // Renamed from "Actual Return" / "Actual Empty Return" (Alan, 2026-09-29:
  // "actual return 这个名字改成return appt"), knowingly against the grain of
  // every OTHER "Appt" in the app meaning a scheduled time (Pickup Appt.) while
  // this one is the recorded ACTUAL time - he confirmed the name change anyway.
  // Still keyed `actual_empty_returned_at`; only the label moved.
  field('actual_empty_returned_at', 'Return Appt', 'P1', ACTIVE_ROLES, { format: 'date-time', headerLabel: '还空约' }),
  // Not a table column any more (Alan, 2026-09-29: "这列删除"): detailOnly, like POD, so the
  // Container page and its timeline still show it. Only the column is gone - the
  // completion it records, and every reader of it, are untouched.
  field('empty_returned_at', 'Empty Returned', 'P0', ACTIVE_ROLES, { format: 'date-time', chip: true, detailOnly: true }),
  field('pickup_company', 'Pickup Company', 'P2', DISPATCH_CLERK, { minWidth: 124, headerLabel: '提柜公司' }),
  field('pod_count', 'POD', 'P1', ACTIVE_ROLES, { format: 'pod', minWidth: 74, detailOnly: true }),
  field('exception_fee_cents', 'Exception Fee', 'P3', FINANCE_ONLY, { format: 'currency', headerLabel: '额外费用' }),
  field('customer_quote_cents', 'Customer Quote', 'P2', FINANCE_ONLY, { format: 'currency', headerLabel: '客户报价' }),
  field('customer_receivable_cents', 'Customer Receivable', 'P2', FINANCE_ONLY, { format: 'currency', headerLabel: '顾客账单金额' }),
  // Widest chip 88px, header 77px; 108 was 18px of nothing. Chip, so two.
  field('remittance_status', 'Remittance', 'P2', FINANCE_ONLY, { minWidth: 88, preferredWidth: 90, chip: true }),
  // The price list's terms for the booked address - chassis days, overweight, DG - next to the fee Finance fills
  // from them (Alan, 2026-10-12: "记得我所说的各种报价和费用这些钱可以被带入到后面的相对的column", then yes to showing the text and not parsing it). First line in the cell, all of it on hover; never turned into a number.
  field('customer_quote_terms', 'Quote Terms', 'P3', FINANCE_ONLY, { headerLabel: '报价说明' }),
  field('customer_special_fee_cents', 'Customer Special Fee', 'P3', FINANCE_ONLY, { format: 'currency', minWidth: 104, preferredWidth: 106, headerLabel: '客户特殊附加费用' }),
  field('service_fee_cents', 'Services Fee', 'P3', FINANCE_ONLY, { format: 'currency', headerLabel: '服务费' }),
  field('customer_special_fee_detail', 'Customer Fee Detail', 'P3', FINANCE_ONLY, { headerLabel: 'Cust. Note' }),
  field('pier_pass_cents', 'PierPass Fee', 'P2', FINANCE_ONLY, { format: 'currency' }),
  field('profit_cents', 'Profit', 'P1', FINANCE_ONLY, { format: 'currency', headerLabel: '利润' }),
  // Hidden from the table, still fully live (Alan, 2026-08-02). Past Due / Pending
  // is already carried by the resting sort order and the row tint, so the column
  // repeated what the row was already saying. The FIELD keeps working everywhere
  // it matters - containerStatusBand reads row.status for the sort, containerStatusTone
  // for the colour, the mobile card still prints it, and the filters still use it -
  // because those read the row, not this column list, which only decides what the
  // desktop grid draws.
  // Detail-only, last in the list: where the reported empty is. The Reported Empty cell shows it after the
  // date; it is not a column of its own.
  field('reported_empty_place', 'Reported Empty Place', 'P3', BACK_OFFICE, { detailOnly: true }),
  field('status', 'Status', 'P0', [], { minWidth: 112, chip: true }),
  field('updated_at', 'Updated', 'P2', ACTIVE_ROLES, { format: 'date-time', minWidth: 88, preferredWidth: 94 }),
]);

// Columns whose value is an address — they all open the map and share the address
// column width. Only the catalog-backed subset below also edits through a dropdown.
export const ADDRESS_FIELD_KEYS = Object.freeze(
  CONTAINER_FIELD_REGISTRY.filter(({ interaction }) => interaction === 'address').map(({ key }) => key),
);

// The catalog-backed address columns: today only Delivery Address, because it is the
// only address a quote is priced against (Alan, 2026-08-01).
export const ADDRESS_CATALOG_FIELD_KEYS = Object.freeze(
  CONTAINER_FIELD_REGISTRY.filter(({ editor }) => editor === 'address').map(({ key }) => key),
);

export function columnUsesAddressCatalog(column) {
  return column?.editor === 'address';
}

export function registryColumnsForRole(role) {
  const normalized = String(role || '').toLowerCase();
  return CONTAINER_FIELD_REGISTRY.filter((definition) => definition.roles.includes(normalized));
}

export function registryDateFieldsForRole(role) {
  return registryColumnsForRole(role)
    .filter(({ format }) => format === 'date-only' || format === 'date-time')
    .map(({ key }) => key);
}

export function columnEditableForRole(column, role) {
  if (!column || typeof column !== 'object' || !column.editor) return false;
  const normalized = String(role || '').toLowerCase();
  return normalized === 'admin' || column.editRoles.includes(normalized);
}

/**
 * Whether this row's customer brings their own chassis — in which case there is
 * nothing for us to assign and Chassis # cannot be filled in (Alan, 2026-08-02).
 *
 * Three answers, not two: yes, no, and nobody has been asked. **Blank is not an
 * answer.** Reading silence as "they brought their own" closes the editor on
 * exactly the Containers that still need a chassis recorded (Alan, 2026-08-10).
 *
 * This lives here, in the registry, because three places have to agree on it and
 * a copy in each is how they drift apart: the table draws the editor from it, the
 * pairing decides whether it may carry the empty's chassis across from it, and
 * `updateMyContainerDashboardField` in functions/src/index.ts refuses the write
 * from its own mirror of it. That server mirror once carried the OLD, inverted
 * meaning for six days while the client carried the new one — every row that drew
 * an editor had its write refused. tests/unit/customer-chassis-server-parity.test.mjs
 * pins the two together now. Do not add a fourth copy.
 */
/**
 * Whether the Container is Cancelled - the one state in which its number may be changed by
 * anyone but Admin (Alan, 2026-10-01: "柜号不能被更改删除除非是本身就是取消状态", then "客户柜号各种
 * 底层的都是管理员有权利修改": Admin may always). The Container # is the box's identity: every
 * Task, POD and charge is keyed to it, so a live Container keeps its number. See
 * containerNumberEditable below; functions/src/index.ts refuses the write the same way.
 */
export function containerIsCancelled(row) {
  const tokens = [row?.ocean_status, row?.status, row?.dashboard_state]
    .map((value) => String(value ?? '').trim().toLowerCase());
  return tokens.includes('cancelled') || tokens.includes('canceled');
}

/** Admin always; everyone else only once the Container is Cancelled. */
export function containerNumberEditable(row, role) {
  return String(role ?? '').trim().toLowerCase() === 'admin' || containerIsCancelled(row);
}

export function rowUsesCustomerOwnChassis(row) {
  const token = String(row?.customer_chassis ?? '').trim().toLowerCase();
  if (!token) return false;
  return token !== 'no';
}
